import React, { useState } from 'react';
import CreateModalTemplate from '@component/gridgio/modal/dialog-modal/create-modal-template';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import styled from 'styled-components';
import Gap from '@component/common/gap';
import LocationPicker from '@component/common/location-picker';
import type { ILocation } from '@util/kakao-map';
import IconTextBlock from '@component/common/block/icon-text-block';
import IconBlock from '@component/common/block/icon-block';
import Canvas from '@design-system/canvas';
import TextBlock from '@component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import TextAreaInput from '@component/common/input/text-area-input';
import { useCreateProjectMutation } from '@hook/gridgio/useApi';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';

const Wrapper = styled.div`
  display: flex;
  width: ${size.GRIDGIO_LARGE_MODAL_WIDTH};
  flex-direction: column;
`;

const LocationContainer = styled.div`
  position: relative;
  width: 100%;
`;

const LocationClearButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: ${color.GRAY_3};
  cursor: pointer;
`;

const LABEL_DESIGN = {
  box: { width: '100%', alignX: 'left' },
  text: { size: 'medium', color: color.BLACK_2, bold: true },
} as const;

const INPUT_DESIGN = {
  box: {
    width: '100%',
    height: '42px',
    padding: `0 ${size.MARGIN_1}`,
    border: true,
    borderWidth: size.BORDER_WIDTH_1,
    borderColor: color.GRAY_2,
    borderRadius: size.BORDER_RADIUS_1,
    alignX: 'left',
  },
  text: { size: 'medium', color: color.BLACK_2 },
} as const;

const ProjectCreateModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState<ILocation | null>(null);
  const [isLocationSelecting, setIsLocationSelecting] = useState(false);
  const mutation = useCreateProjectMutation();

  const handleClose = () => {
    if (!mutation.isPending) dispatch(modalActions.hideModal());
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || mutation.isPending) return;

    setErrorMessage('');
    try {
      await mutation.mutateAsync({
        name: trimmedName,
        description: description.trim() || undefined,
        place: location?.name || undefined,
        address: location?.address || undefined,
        coordinate: location ? { latitude: location.latitude, longitude: location.longitude } : undefined,
      });
      dispatch(modalActions.hideModal());
    } catch {
      setErrorMessage('프로젝트를 만들지 못했습니다. 다시 시도해주세요.');
    }
  };

  if (isLocationSelecting) {
    return (
      <Wrapper>
        <LocationPicker
          value={location}
          handleConfirm={(selected) => {
            setLocation(selected);
            setIsLocationSelecting(false);
          }}
          handleCancel={() => setIsLocationSelecting(false)}
        />
      </Wrapper>
    );
  }

  return (
    <CreateModalTemplate
      title='새 프로젝트'
      confirmText='프로젝트 만들기'
      errorMessage={errorMessage}
      isPending={mutation.isPending}
      confirmDisabled={!name.trim()}
      handleCancel={handleClose}
      handleConfirm={() => void handleCreate()}
      input={
        <>
          <TextBlock text='프로젝트 이름' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextInput
            name='project-name'
            value={name}
            placeholder='프로젝트 이름을 입력해주세요.'
            design={INPUT_DESIGN}
            onChange={(event) => setName(event.target.value)}
          />
          <Gap height={size.MARGIN_2} />
          <TextBlock text='프로젝트 설명 (선택)' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextAreaInput
            name='project-description'
            value={description}
            placeholder='프로젝트 설명을 입력해주세요.'
            row={3}
            max={500}
            design={{
              ...INPUT_DESIGN,
              box: { ...INPUT_DESIGN.box, height: '80px', padding: size.MARGIN_1 },
            }}
            onChange={(event) => setDescription(event.target.value)}
          />
          <Gap height={size.MARGIN_2} />
          <TextBlock text='프로젝트 위치 (선택)' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <LocationContainer>
            <Canvas
              theme={ETheme.WHITE}
              width='100%'
              padding={location ? `${size.MARGIN_1} 40px ${size.MARGIN_1} ${size.MARGIN_1}` : size.MARGIN_1}
              border
              handleClick={() => {
                if (!mutation.isPending) setIsLocationSelecting(true);
              }}>
              <IconTextBlock
                icon={location ? 'map-pin' : 'map-pin-plus'}
                text={location ? location.name || '선택한 위치' : '현장 위치 찾기'}
                design={{
                  box: { width: '100%', alignX: 'left', gap: '5px' },
                  text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
                  icon: { size: 'medium', color: color.BLACK_2 },
                }}
              />
              <Gap height='5px' />
              <TextBlock
                text={
                  location
                    ? location.address || '지도에서 지정한 위치'
                    : '현장 주소를 검색하거나 지도에서 위치를 선택하세요.'
                }
                design={{ box: { width: '100%', alignX: 'left' }, text: { size: 'small', color: color.GRAY_1 } }}
              />
            </Canvas>
            {location && (
              <LocationClearButton type='button' disabled={mutation.isPending} onClick={() => setLocation(null)}>
                <IconBlock icon='close' design={{ icon: { size: 'tiny', color: color.BLACK_2 } }} />
              </LocationClearButton>
            )}
          </LocationContainer>
        </>
      }
    />
  );
};

export default ProjectCreateModal;
